import { useTranslation } from 'react-i18next';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
export default function AboutPage() {
  const { t } = useTranslation();

  const schema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "关于精品视频",
    "description": "精品视频成立于2020年，是一家专注于高清视频内容聚合与分发的互联网平台，服务超过5000万用户。",
    "url": "https://www.jingpinshipin.com/about",
  };

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "精品视频",
    "url": "https://www.jingpinshipin.com",
    "description": "精品视频汇聚海量高清影视、教育、娱乐内容，画质清晰、更新及时、分类精准。",
    "foundingDate": "2020",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "建国路88号SOHO现代城A座18层",
      "addressLocality": "北京",
      "addressRegion": "朝阳区",
      "postalCode": "100022",
      "addressCountry": "CN",
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+86-10-8888-6666",
      "contactType": "customer service",
      "email": "contact@jingpinshipin.com",
      "availableLanguage": "Chinese",
    },
  };

  const reputationItems = [
    { label: '服务用户', value: '5000万+', icon: 'ri-user-heart-line' },
    { label: '内容时长', value: '10万+小时', icon: 'ri-film-line' },
    { label: '合作方', value: '200+', icon: 'ri-team-line' },
    { label: '日活用户', value: '300万+', icon: 'ri-fire-line' },
  ];

  return (
    <>
      <SEOHead
        title="关于我们"
        description="精品视频成立于2020年，是一家专注于高清视频内容聚合与分发的互联网平台。了解精品视频的品牌故事、平台信誉与联系方式。"
        keywords="精品视频,关于我们,品牌介绍,联系方式,视频平台"
        canonicalUrl="https://www.jingpinshipin.com/about"
        schema={schema}
      />
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-12">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: t('home_breadcrumb'), href: '/' },
                { label: t('about_breadcrumb') },
              ]}
            />

            <section className="mt-6">
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900">
                {t('brand_story_title')}
              </h1>
              <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  <p className="text-foreground-600 leading-relaxed text-sm md:text-base">
                    {t('brand_story')}
                  </p>
                </div>
                <div className="rounded-lg overflow-hidden aspect-[4/3]">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20media%20company%20office%20with%20large%20video%20screens%20displaying%20content%2C%20creative%20workspace%2C%20warm%20ambient%20lighting%2C%20professional%20environment%2C%20clean%20contemporary%20design%2C%20team%20collaboration%20atmosphere&width=800&height=600&seq=about-brand-image&orientation=landscape"
                    alt="精品视频品牌"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </section>

            <section className="mt-16">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 text-center">
                {t('reputation_title')}
              </h2>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
                {reputationItems.map((item, i) => (
                  <div key={i} className="flex flex-col items-center p-6 rounded-lg bg-background-100 border border-background-200/70">
                    <span className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-3">
                      <i className={`${item.icon} text-lg`}></i>
                    </span>
                    <span className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">{item.value}</span>
                    <span className="text-sm text-foreground-500 mt-1">{item.label}</span>
                  </div>
                ))}
              </div>
            </section>

            <section className="mt-16">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 text-center">
                {t('contact_title')}
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
                <div className="flex flex-col gap-5 p-6 rounded-lg bg-background-100 border border-background-200/70">
                  <div className="flex items-start gap-3">
                    <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-map-pin-line text-base"></i>
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">地址</p>
                      <p className="text-sm text-foreground-500 mt-0.5">{t('contact_address')}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-phone-line text-base"></i>
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">电话</p>
                      <p className="text-sm text-foreground-500 mt-0.5">{t('contact_phone')}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-mail-line text-base"></i>
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">邮箱</p>
                      <p className="text-sm text-foreground-500 mt-0.5">{t('contact_email')}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-wechat-line text-base"></i>
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">微信</p>
                      <p className="text-sm text-foreground-500 mt-0.5">{t('contact_wechat')}</p>
                    </div>
                  </div>
                </div>

                <div className="rounded-lg overflow-hidden h-[300px] lg:h-full">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3061.658295543165!2d116.4667!3d39.9087!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x35f052f0413a4c2b%3A0x4b29fc2a4ca6e7de!2sSOHO%20Modern%20City!5e0!3m2!1szh-CN!2scn!4v1690000000000"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="精品视频位置"
                    className="rounded-lg"
                  ></iframe>
                </div>
              </div>
            </section>
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}